import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Button,
  IconButton,
  Box,
  Typography,
  MenuItem,
  Select,
  InputLabel,
  FormControl,
} from "@mui/material";
import { useState, useEffect } from "react";
import AddIcon from "@mui/icons-material/Add";
import DeleteIcon from "@mui/icons-material/Delete";

export default function EditDialog<T extends Record<string, any>>({
  open,
  item,
  onSave,
  onClose,
}: {
  open: boolean;
  item: T | null;
  onSave: (updated: T) => void;
  onClose: () => void;
}) {
  const [form, setForm] = useState<T>(item ?? ({} as T));

  useEffect(() => {
    if (item) setForm(item);
  }, [item]);

  const handleChange = (key: keyof T, value: string) => {
    setForm((prev) => ({ ...prev, [key]: value }));
  };

  const handlePointChange = (index: number, value: string) => {
    setForm((prev) => {
      const points = [...(prev.points || [])];
      points[index] = value;
      return { ...prev, points };
    });
  };

  const addPoint = () => {
    setForm((prev) => ({ ...prev, points: [...(prev.points || []), ""] }));
  };

  const removePoint = (index: number) => {
    setForm((prev) => {
      const points = [...(prev.points || [])];
      points.splice(index, 1);
      return { ...prev, points };
    });
  };

  const handleSave = () => onSave(form);

  if (!item) return null;

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="sm">
      <DialogTitle>Edit Item</DialogTitle>
      <DialogContent sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
        {Object.keys(form).map((key) => {
          if (key === "points") return null;

          // Type dropdown example (optional)
          if (key === "type") {
            return (
              <FormControl fullWidth key={key}>
                <InputLabel>Type</InputLabel>
                <Select
                  value={form[key]}
                  label="Type"
                  onChange={(e) => handleChange(key as keyof T, e.target.value)}
                >
                  <MenuItem value="Education">Education</MenuItem>
                  <MenuItem value="Work">Work</MenuItem>
                </Select>
              </FormControl>
            );
          }

          // Year input
          if (key === "year") {
            return (
              <TextField
                key={key}
                label="Year"
                type="number"
                value={form[key]}
                inputProps={{ min: 1900, max: 2099, step: 1 }}
                onChange={(e) => {
                  const val = e.target.value.slice(0, 4);
                  handleChange(key as keyof T, val);
                }}
                fullWidth
              />
            );
          }

          // Default text input
          return (
            <TextField
              key={key}
              label={key.charAt(0).toUpperCase() + key.slice(1)}
              value={form[key]}
              onChange={(e) => handleChange(key as keyof T, e.target.value)}
              fullWidth
            />
          );
        })}

        {/* Live preview if there is a "cover" field */}
        {"cover" in form && form.cover && (
          <Box sx={{ mt: 2, textAlign: "center" }}>
            <Typography variant="subtitle2" gutterBottom>
              Cover Preview
            </Typography>
            <img
              src={form.cover}
              alt="Cover Preview"
              style={{ maxWidth: "100%", maxHeight: 300, borderRadius: 4 }}
            />
          </Box>
        )}

        {/* Points editing (optional) */}
        {form.points && (
          <Box>
            <Typography variant="subtitle1">Points:</Typography>
            {form.points.map((p: string, i: number) => (
              <Box key={i} display="flex" alignItems="center" gap={1} mb={1}>
                <TextField
                  fullWidth
                  value={p}
                  onChange={(e) => handlePointChange(i, e.target.value)}
                />
                <IconButton size="small" onClick={() => removePoint(i)}>
                  <DeleteIcon fontSize="small" />
                </IconButton>
              </Box>
            ))}
            <Button startIcon={<AddIcon />} onClick={addPoint}>
              Add Point
            </Button>
          </Box>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>
        <Button onClick={handleSave} variant="contained">
          Save
        </Button>
      </DialogActions>
    </Dialog>
  );
}
